import type { UpgradeId } from './UpgradeDefinition';

export type UpgradesSnapshot = ReadonlySet<UpgradeId>;
export type UpgradesListener = (snap: UpgradesSnapshot) => void;

/**
 * Tracks which permanent ship modules the player has installed. Decoupled
 * from inventory and wallet so any system (shop, recycler tab, future
 * weapons) can subscribe to changes without depending on UI internals.
 */
export class ShipUpgrades {
  private readonly installed = new Set<UpgradeId>();
  private readonly listeners = new Set<UpgradesListener>();

  has(id: UpgradeId): boolean {
    return this.installed.has(id);
  }

  install(id: UpgradeId): boolean {
    if (this.installed.has(id)) return false;
    this.installed.add(id);
    this.emit();
    return true;
  }

  /** Replace the entire installed-upgrades set. Used by the save/load
   *  system — emits a single update for the UI when finished. */
  restore(ids: ReadonlyArray<UpgradeId>): void {
    this.installed.clear();
    for (const id of ids) this.installed.add(id);
    this.emit();
  }

  snapshot(): UpgradesSnapshot {
    return new Set(this.installed);
  }

  subscribe(listener: UpgradesListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const listener of this.listeners) listener(snap);
  }
}
